import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { getToken, isLoggedIn } from '../../authetication/loginState';
import Resume from '../../images/resume.png';
import { useMediaQuery } from '@mui/material';

const SectionTitle = ({ text, subtitle }) => (
    <div className="section-title-container" style={{ marginTop: '-40px'}}>
        <h1 className="section-title">{text}</h1>
        <p className="section-subtitle2">{subtitle}</p>
    </div>
);

const INTERVIEW_MODE_SETS = [
  {
    key: 'nursing',
    defaultLevel: 30,
    match: (role) => /(nurs|\brn\b|\bcna\b)/i.test(role || ''),
    modes: [
      { level: 20, label: 'Recruiter Screen' },
      { level: 30, label: 'Nursing Manager Interview' },
      { level: 35, label: 'Specialty Clinical' },
      { level: 15, label: 'Salary Negotiation' },
    ],
    about: [
      {
        title: 'Recruiter Screen',
        summary: 'Qualifications, availability, motivation, and pay.',
        detail: `The first call, usually with HR. Pick this to nail your quick pitch and "why this hospital" before you reach the manager. It's the filter round, so practice being sharp and concise.`,
      },
      {
        title: 'Nursing Manager Interview',
        summary: 'Behavioral, situational, and culture fit questions.',
        detail: `The main round with the hiring manager, right after the recruiter screen. Pick this to practice telling your stories the STAR way, since this is where most offers are won or lost.`,
      },
      {
        title: 'Specialty Clinical',
        summary: 'Clinical knowledge and patient scenarios by specialty.',
        detail: `Common for ICU, ER, L&D, and other specialty units, often a second-round deep-dive. Pick this to practice clinical reasoning, prioritization, and safety under pressure.`,
      },
      {
        title: 'Salary Negotiation',
        summary: 'Focus on salary and benefits discussion.',
        detail: `Comes near the end, once they want you. Pick this to practice talking pay, shift differentials, and benefits without underselling yourself.`,
      },
    ],
  },
  {
    key: 'flightAttendant',
    defaultLevel: 45, // Live Virtual = "best mode for overall practice"
    match: (role) => /(flight attendant|\bfa\b|cabin crew)/i.test(role || ''),
    modes: [
      { level: 40, label: 'HireVue Interview' },
      { level: 45, label: 'Live Virtual Interview' },
      { level: 5,  label: 'Standard Interview' },
      { level: 15, label: 'Salary Negotiation' },
    ],
    about: [
      {
        title: 'HireVue Interview',
        summary: 'Replicates the HireVue recorded screen process.',
        detail: `Airlines use this as a first screen, before any live round. One-way recorded questions to camera, no follow-ups — just you and a timer.`,
      },
      {
        title: 'Live Virtual Interview',
        summary: 'Behavioral, situational, and values fit.',
        detail: `Usually comes after the HireVue and recruiter phone screen. The best mode for overall practice — a full conversational round where you drill your STAR stories and service scenarios.`,
      },
      { title: 'Standard Interview', summary: 'Mix of Behavioral and Technical.' },
      { title: 'Salary Negotiation', summary: 'Focus on Salary and Benefits Discussion.' },
    ],
  },
  {
    key: 'default',
    match: () => true,
    modes: [
      { level: 0, label: 'Behavioral' },
      { level: 5, label: 'Standard' },
      { level: 10, label: 'Technical' },
      { level: 15, label: 'Salary Negotiation' },
    ],
    about: [
      { title: 'Behavioral Mode', summary: 'All Behavioral Questions.' },
      { title: 'Standard Mode', summary: 'Mix of Behavioral and Technical.' },
      { title: 'Technical Mode', summary: 'All Technical Questions.' },
      { title: 'Salary Negotiation', summary: 'Focus on Salary and Benefits Discussion.' },
    ],
  },
];

const resolveModeSet = (role) =>
  INTERVIEW_MODE_SETS.find((s) => s.key !== 'default' && s.match(role)) ||
  INTERVIEW_MODE_SETS.find((s) => s.key === 'default');

const NoraUploader = ({
  position,
  setPosition,
  companyName,
  setCompanyName,
  jobDescription,
  setJobDescription,
  setTechnicalLevel,
  technicalLevel,
  handleFileChange,
  triggerFileInput,
  handleTechnicalLevelChange,
  fileName,
  setFileName,
  resume,     
  setResume,
  handleGetStarted,
  isFormComplete,
  isModal,
  salaryRange,
  setSalaryRange,
  currency,
  setCurrency,
  location,
  setLocation,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showTechnicalModal, setShowTechnicalModal] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [isLoadingResume, setIsLoadingResume] = useState(false);
  const [companyData, setCompanyData] = useState(null);

  // Role the user gave at onboarding (stored in localStorage). Drives which
  // interview modes + descriptions show. Falls back to the default set.
  const desiredRole = useMemo(() => localStorage.getItem('desiredRole') || '', []);
  const modeSet = useMemo(() => resolveModeSet(desiredRole), [desiredRole]);
  const modeRows = [];
  for (let i = 0; i < modeSet.modes.length; i += 2) {
    modeRows.push(modeSet.modes.slice(i, i + 2));
  }

  // If the current level isn't valid for this role's set, default to its first mode.
  useEffect(() => {
    const levels = modeSet.modes.map((m) => m.level);
    if (!levels.includes(technicalLevel)) {
      setTechnicalLevel(modeSet.defaultLevel ?? modeSet.modes[0].level);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeSet.key]);

  const helpAnchorRef = useRef(null);

  // While the help modal is open, hide the surrounding MUI modal (if any) so they
  // don't stack. Hidden, not unmounted — the form keeps all its state.
  useEffect(() => {
    const muiModal = helpAnchorRef.current?.closest('.MuiModal-root');
    if (!muiModal) return; // inline render (no interviews) — nothing to hide
    muiModal.style.visibility = showTechnicalModal ? 'hidden' : '';
    return () => { muiModal.style.visibility = ''; };
  }, [showTechnicalModal]);

  const handleCompanyBlur = async () => {
    if (!companyName.trim()) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/Chat/enrichCompany?name=${encodeURIComponent(companyName)}`
      );
      const d = res.data;
      const enriched = {
        size: d.size,
        employee_count: d.employee_count,
        founded: d.founded,
        industry: d.industry_v2 || d.industry,
        location: d.location?.name,
        summary: d.summary,
        headline: d.headline,
        total_funding_raised: d.total_funding_raised,
        latest_funding_stage: d.latest_funding_stage,
      };
      setCompanyData(enriched);
      localStorage.setItem('companyData', JSON.stringify(enriched)); 
    } catch (e) {
      // fail silently
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange({ target: { files: [file] } });
    }
  };

  useEffect(() => {
  const loadSavedResume = async () => {
    try {
      setIsLoadingResume(true);
      const token = getToken();
      
      if (!token) {
        return; // User not logged in
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/Chat/getUserResume`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.hasResume && response.data.resumeUrl) {
        // Download the resume file from S3
        const resumeResponse = await axios.get(response.data.resumeUrl, {
          responseType: 'blob'
        });

        // Convert blob to base64 and store in localStorage
        const reader = new FileReader();
        reader.onloadend = () => {
          localStorage.setItem('resume', reader.result);
          setResume(reader.result);
          
          // ✅ EXTRACT AND FORMAT THE FILENAME
          // Extract filename from URL: https://...s3.../resume-Resume-2024-latest-{guid}.docx
          const urlParts = response.data.resumeUrl.split('/');
          const fullFileName = urlParts[urlParts.length - 1]; // "resume-Resume-2024-latest-93ee3c17-0e2a-4e97-ae41-413b5ce2be36.docx"
          
          // Remove the "resume-" prefix and the GUID suffix
          // Pattern: resume-{NAME}-{GUID}.{ext}
          const match = fullFileName.match(/^resume-(.+)-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(\..+)$/i);
          
          let displayName;
          if (match) {
            // match[1] = "Resume-2024-latest"
            // match[2] = ".docx"
            displayName = `${match[1]}${match[2]}`; // "Resume-2024-latest.docx"
          } else {
            // Fallback if pattern doesn't match
            displayName = fullFileName;
          }
          
          setFileName(displayName);
          
          console.log('Loaded saved resume from user profile');
        };
        reader.readAsDataURL(resumeResponse.data);
      }
    } catch (error) {
      console.error('Error loading saved resume:', error);
      // Don't show error to user - this is a convenience feature
    } finally {
      setIsLoadingResume(false);
    }
  };

  loadSavedResume();
}, [setFileName, setResume]);

  return (
    <>
    <span ref={helpAnchorRef} style={{ display: 'none' }} />
    <SectionTitle
        text="AI Mock Interviewer"
        subtitle="Simply upload resume + job description. Nora will simulate a realistic interview."
    />

      <div className="form-container">
        <div className="form-section left-section">
          <div className={`form-group file-upload ${isDragging ? 'dragging' : ''}`}
            onClick={triggerFileInput}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="fileInput"
              className="form-control"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx"
              style={{ display: 'none' }}
            />
            <div className="file-upload-content">
              <img src={Resume} alt="PDF Icon" className="pdf-icon" />
              {fileName ? (
                <p className="file-name">{fileName}</p>
              ) : (
                <>
                  <p>{isDragging ? 'Drop your resume here' : 'Click to upload your resume here'}</p>
                  <p className="file-size">up to 10 MB (pdf or docx)</p>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="right-sections">
            <div className="form-section">
                <div className="form-group">
                    <label>
                        Position <span className="required-asterisk">*</span>
                    </label>
                    <input
                        className="form-control"
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        placeholder="ex. UX Design Intern" />
                </div>
            </div>
            <div className="form-section">
                <div className="form-group">
                    <label>
                        Company Name <span className="required-asterisk">*</span>
                    </label>
                    <input
                        className="form-control"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="ex. Google" />
                </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Choose interview mode <span className="required-asterisk">*</span></span>
                      <span 
                        className="question-mark"
                        onClick={() => setShowTechnicalModal(true)}
                      >
                        ?
                      </span>
                  </label>
                  <div className="technical-level-interview-buttons">
                      {modeRows.map((row, ri) => (
                          <div className="technical-level-row" key={ri}>
                              {row.map((m) => (
                                  <button
                                      key={m.level}
                                      type="button"
                                      className={`technical-level-interview-btn ${ri >= 1 ? 'salary-negotiation' : ''} ${technicalLevel === m.level ? 'selected' : ''}`}
                                      onClick={() => setTechnicalLevel(m.level)}
                                  >
                                      {m.label}
                                  </button>
                              ))}
                          </div>
                      ))}
                  </div>
              </div>
            </div>

            {technicalLevel === 15 && (
              <>
              <div className="form-section">
                <div className="form-group">
                  <label>Annual Salary Range</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select
                      className="form-control"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      style={{ 
                        width: '100px', 
                        height: '42px', 
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '10px',
                        fontSize: 'inherit'
                      }}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="AED">AED</option>
                      <option value="INR">INR</option>
                      <option value="CAD">CAD</option>
                      <option value="AUD">AUD</option>
                    </select>
                    <input
                      className="form-control"
                      value={salaryRange}
                      onChange={(e) => setSalaryRange(e.target.value)}
                      placeholder="eg: 100K - 200K"
                      style={{ flex: 1 }} />
                  </div>
                </div>
                </div>
                <div className="form-section">
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      className="form-control"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="eg: San Francisco, USA" />
                  </div>
                </div>
              </>
            )}

            <div className="form-section">
                <div className="form-group">
                    <label>Job Description</label>
                    <textarea
                        className="form-control"
                        value={jobDescription}
                        onChange={(e) => setJobDescription(e.target.value)}
                        placeholder="Enter the position's job description - gives our AI the ultimate context!" />
                </div>
            </div>
        </div>
    </div>
    <div className="get-started-button-container">
        <button
            className={`${isFormComplete ? 'nora-button-style' : 'nora-button-disabled'}`}
            onClick={async () => {
              const tokenValid = await isLoggedIn();
              if (!tokenValid) {
                window.location.href = "/login?reason=session_expired";
                return;
              }
              await handleCompanyBlur();
              handleGetStarted();
            }}
            disabled={!isFormComplete}
        >
            Get Started
        </button>
    </div>

    {showTechnicalModal && createPortal(
        <div className="modal-overlay" style={{ zIndex: 2000 }} onClick={() => setShowTechnicalModal(false)}>
            <div className="technical-modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowTechnicalModal(false)}>×</button>
                <h3 className="mode-about-heading">About Interview Modes</h3>
                <ul className="mode-about-list">
                    {modeSet.about.map((a) => (
                        <li key={a.title} className="mode-about-item">
                            <p className="mode-about-title">{a.title}</p>
                            <p className="mode-about-summary">{a.summary}</p>
                            {a.detail && <p className="mode-about-detail">{a.detail}</p>}
                        </li>
                    ))}
                </ul>
            </div>
        </div>,
        document.body
    )}
    </>
)};

export default NoraUploader;